import React, { useMemo, useRef, useState } from "react";
import { IconButton } from "./IconButton";
import {
  Foutmelding,
  maakFoutmelding,
  metVoorvoegsel,
} from "../../utils/foutmelding";
import styles from "./FoutmeldingBlok.module.css";

interface FoutmeldingBlokProps {
  /**
   * Uit `maakFoutmelding`. Een losse tekst (eigen controle van een formulier,
   * geen fout van de server) toont geen technisch blok.
   */
  melding?: Foutmelding | string;
  /**
   * Of de ruwe fout zelf (bv. `error` van React Query): het blok maakt er één
   * keer per foutobject een melding van, dus de tijd blijft staan.
   */
  fout?: unknown;
  /** Met `fout`: een eigen zin als hoofdtekst (zie `maakFoutmelding`). */
  eigenTekst?: string;
  /** Met `fout`: een eigen begin vóór de hoofdtekst. */
  voorvoegsel?: string;
  /** Bestaande foutopmaak van de module; zonder className en style de standaard. */
  className?: string;
  style?: React.CSSProperties;
  role?: React.AriaRole;
}

/**
 * B12 / Q13: de foutmelding op de plek waar hij al stond, met eronder
 * "Technische details" om te kopiëren en naar Niels te sturen.
 */
export function FoutmeldingBlok({
  melding,
  fout,
  eigenTekst,
  voorvoegsel,
  className,
  style,
  role,
}: FoutmeldingBlokProps) {
  // De details pas renderen als ze open zijn: de servermelding staat dan niet
  // dubbel in de pagina (hoofdtekst én technisch blok) voor schermlezers.
  const [open, setOpen] = useState(false);
  const [kopieStatus, setKopieStatus] = useState<
    "" | "gekopieerd" | "geselecteerd"
  >("");
  const technischRef = useRef<HTMLPreElement>(null);
  const getoond = useMemo<Foutmelding | string>(() => {
    if (melding !== undefined) return melding;
    const gemaakt = maakFoutmelding(fout, eigenTekst);
    return voorvoegsel ? metVoorvoegsel(voorvoegsel, gemaakt) : gemaakt;
  }, [melding, fout, eigenTekst, voorvoegsel]);
  const { hoofdtekst, technisch } =
    typeof getoond === "string"
      ? { hoofdtekst: getoond, technisch: "" }
      : getoond;

  const selecteerTechnisch = () => {
    const el = technischRef.current;
    const selectie = window.getSelection();
    if (!el || !selectie) return;
    const bereik = document.createRange();
    bereik.selectNodeContents(el);
    selectie.removeAllRanges();
    selectie.addRange(bereik);
  };

  const kopieer = async () => {
    try {
      await navigator.clipboard.writeText(technisch);
      setKopieStatus("gekopieerd");
    } catch {
      // Geen klembord (oude browser, geen toestemming): de tekst selecteren,
      // dan werkt Ctrl+C.
      selecteerTechnisch();
      setKopieStatus("geselecteerd");
    }
  };

  const klasse = className ?? (style ? undefined : styles.standaard);

  return (
    <div className={klasse} style={style} role={role}>
      <div className={styles.hoofdtekst}>{hoofdtekst}</div>
      {technisch && (
        <details
          className={styles.details}
          open={open}
          onToggle={(e) => setOpen(e.currentTarget.open)}
        >
          <summary>Technische details</summary>
          {open && (
            <>
              <pre ref={technischRef} className={styles.technisch}>
                {technisch}
              </pre>
              <div className={styles.acties}>
                <IconButton
                  type="button"
                  variant="neutral"
                  onClick={kopieer}
                  style={{ padding: "0.2rem 0.75rem", fontSize: "0.8rem" }}
                >
                  Kopieer
                </IconButton>
                {kopieStatus === "gekopieerd" && (
                  <span className={styles.status}>Gekopieerd</span>
                )}
                {kopieStatus === "geselecteerd" && (
                  <span className={styles.status}>
                    Tekst geselecteerd, kopieer met Ctrl+C (Mac: Cmd+C)
                  </span>
                )}
              </div>
            </>
          )}
        </details>
      )}
    </div>
  );
}

export default FoutmeldingBlok;
